import _ from "lodash";
// third party
import { graphql } from "lib/apolloClient";
import update from "immutability-helper";

// utils
import { prepareLoadMoreFeed } from "Utils";
import { getIsFromClassroom } from "UnitPlans/modules/Utils";
import {
  getUnitDetailsFromUnitPlanField,
  getUnitPlanFromAssessment,
  updateAssessmentTemplate,
  getFieldValue,
  changeDisciplinaryContentToMultiField,
  getEntityTypeForAI,
  getUnitPlanDetails,
  updateTemplateForLearningGoals,
  normalizePlannerElementNodeSets,
  toPlannerElementNodeFilters,
  withComputedIsLeafForNodes,
  getObjectivesFromNodes,
} from "./Utils";
import { replaceLearningGoalsFields } from "Worksheet/modules/WorksheetUtils";

// constants
import {
  CACHE_AND_NETWORK,
  CURRICULUM_TYPE_PYP,
  PLANNER_ELEMENT_NESTED_CHECKLIST,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_ACHIEVEMENT_LEVEL,
} from "Constants/stringConstants";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";

// modules
import {
  getFlatUnitPlanFeedForAssessmentQuery,
  getAssessmentQuery,
  getPlannerElementSetsOfCurriculumQuery,
  getBenchmarkSetOfCurriculumQuery,
  getCurriculumStandardsQuery,
  getPypPlannerElementNodesOfCurriculumQuery,
} from "Assessments/modules/AssessmentQuery";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import { getVariablesForLocalisedPlannerTemplates } from "modules/Services";
import { getLocalisedTemplate } from "IBPlanner/modules/IBPlannerModule";
import { getAIAnalysisItem } from "ClassRoom/modules/utils";
import { getLearningCourseVariables } from "LearningCourse/modules/LearningCourseUtils";
import { getLearningCourseDetailsFromCache } from "LearningCourse/modules/LearningCourseGraphqlHelpers";

export const getFlatUnitPlanFeedForAssessmentEnhancer = graphql(
  getFlatUnitPlanFeedForAssessmentQuery,
  {
    name: "getFlatUnitPlanFeedForAssessment",
    skip: ({ skipUnitPlanFeedQuery }) => skipUnitPlanFeedQuery,
    options: ({ unitPlanFeedVariables }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: unitPlanFeedVariables,
      context: {
        useHttpLink: true,
      },
    }),
    props({ getFlatUnitPlanFeedForAssessment }) {
      const {
        node,
        networkStatus,
        variables,
      } = getFlatUnitPlanFeedForAssessment;

      const unitPlanEdges = _.get(node, "flatUnitPlans.edges", []);
      const unitPlans = _.map(unitPlanEdges, ({ node }) => node);

      const pageInfo = _.get(node, "flatUnitPlans.pageInfo", {});
      const { hasNextPage = false, endCursor } = pageInfo;

      return {
        unitPlans,
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          networkStatus
        ),
        hasNextPage,
        networkStatus,
        isLoadingMoreUnitPlans: networkStatus == 3,
        loadMoreUnitPlanFeed: prepareLoadMoreFeed({
          query: getFlatUnitPlanFeedForAssessmentQuery,
          variables: {
            ...variables,
            after: endCursor,
          },
          queryData: getFlatUnitPlanFeedForAssessment,
          key: "flatUnitPlans",
        }),
      };
    },
  }
);

export const getAssessmentDetailsForAIEnhancer = graphql(getAssessmentQuery, {
  name: "getAssessment",
  skip: ({ leId }) => _.isEmpty(leId),
  options: ({ leId, collaboratorsFilter, portalType }) => ({
    // cache-first is used because this query will always get called in AssessmentDetails.js and latest data will be available in cache
    // if we use cache-and-network, it will make a network call and override the cache data
    // the reason to not use getAssessmentDetailsFromCache directly is this component won't get re-rendered if cache data changes
    fetchPolicy: "cache-first",
    variables: { id: leId, collaboratorsFilter, portalType },
  }),
  props: ({ getAssessment, ownProps }) => {
    const {
      t,
      leId,
      mode,
      sourceFrom,
      curriculumType,
      courseId,
      selectedCourse,
      courseGradeFilters,
      plannerElementSets,
      i18n,
      userType,
      entitySubType = "TASK_CREATION",
      isQuestionAssistant = false,
      isData = true,
      isLoading = false,
      conversationMetadata,
      collaboratorsFilter,
      learningCourseId,
      academicYearId,
    } = ownProps;

    let assessmentDetails = getAssessmentDetailsFromCache({
      id: leId,
      collaboratorsFilter,
    });

    const aiConversation = _.get(assessmentDetails, "aiConversation.edges", []);

    const aiConversationId =
      _.get(ownProps, "aiConversationId") || // priority to ownProps.aiConversationId (student end case)
      _.get(
        _.find(aiConversation, ({ node }) => {
          const { entitySubType: nodeEntitySubType } = node || {};
          // nodeEntitySubType for old assessments will be null
          return !nodeEntitySubType || nodeEntitySubType === entitySubType;
        }),
        "node.id"
      );

    // generic case
    let course = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters,
    });

    if (_.isEmpty(course)) {
      // calendar case
      if (!_.isEmpty(selectedCourse)) {
        course = selectedCourse;
      } else {
        // course flow case
        const learningCourseVariables = getLearningCourseVariables({
          learningCourseId,
          academicYearId,
        });

        const learningCourse = getLearningCourseDetailsFromCache(
          learningCourseVariables
        );

        course = learningCourse;
      }
    }

    const gradeId =
      _.get(course, "primaryGrade.id") ||
      _.get(course?.grades, "0.id") ||
      _.get(assessmentDetails, "grades.0.id");

    const assessmentType = _.get(assessmentDetails, "assessmentType.value");
    const taskTypeGroup = _.get(assessmentDetails, "taskType.type");
    const groupType = _.get(assessmentDetails, "type");
    const templateId = _.get(assessmentDetails, "templateId", null);

    const unitPlan = getUnitPlanFromAssessment({ assessmentDetails });

    const unitPlanTemplateId = _.get(unitPlan, "templateId", null);

    const unitPlanDetails = getUnitDetailsFromUnitPlanField({
      assessmentDetails,
    });

    const { unitPlanFields } = unitPlanDetails || {};

    const resolvedSubjectNodes = _.get(
      _.find(unitPlanFields, { uid: "subjects" }),
      "resolvedMinimalTree.subjects",
      []
    );

    let templates = getPlannerTemplateFromCache(
      { ids: _.compact([templateId]) },
      true
    );

    if (mode !== "view" && !_.isEmpty(templates)) {
      const unitPlanTemplate = getPlannerTemplateFromCache(
        getVariablesForLocalisedPlannerTemplates({
          templates: [unitPlanTemplateId],
          language: i18n.language,
        })
      );
      templates = [...templates, unitPlanTemplate];
    }

    if (_.isEmpty(templates)) {
      templates = getPlannerTemplateFromCache(
        { ids: _.compact([templateId]) },
        true
      );
    }

    const originalTemplate = _.get(
      _.find(templates, { id: templateId }),
      "body",
      {}
    );

    const isFromClassroom = getIsFromClassroom({
      sourceFrom,
      template: originalTemplate,
    });

    const dynamicTemplate = updateTemplateForLearningGoals({
      template: _.cloneDeep(originalTemplate),
      templateId,
    });

    const localisedAssessmentTemplate = _.get(
      getLocalisedTemplate({
        template: { body: _.cloneDeep(dynamicTemplate) },
        t,
        templateType: "le",
      }),
      "body",
      {}
    );

    /** As we have learning goals on question level as well, so we are mutating learning goals fields in assessmentDetail.allFields/unitPlanDetails.unitPlanFields and adding question's learningGoals fields */
    if (isQuestionAssistant) {
      const updatedAssessmentFields = replaceLearningGoalsFields({
        template: dynamicTemplate,
        fields: _.get(assessmentDetails, "allFields", []),
        replacementFields: _.get(conversationMetadata, "allFields", []),
      });

      assessmentDetails = update(assessmentDetails, {
        allFields: {
          $set: updatedAssessmentFields,
        },
      });
    }

    const assessmentTemplate = updateAssessmentTemplate({
      t,
      template: localisedAssessmentTemplate,
      assessmentType,
      taskTypeGroup,
      isFromClassroom,
      curriculumType,
      resourceFields: _.get(assessmentDetails, "allFields", []),
      mode: "edit",
      templateId,
      plannerElementSets,
      subjects: _.get(course, "subjects", []),
      userType,
      assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
    });

    let _unitPlanTemplate = _.find(templates, { id: unitPlanTemplateId });

    if (_.isEmpty(_unitPlanTemplate) && !_.isEmpty(unitPlanTemplateId)) {
      _unitPlanTemplate = getPlannerTemplateFromCache(
        getVariablesForLocalisedPlannerTemplates({
          templates: [unitPlanTemplateId],
          language: i18n.language,
        })
      );
    }

    const unitPlanTemplate = _.get(
      changeDisciplinaryContentToMultiField({
        t,
        template: _unitPlanTemplate,
        unitType: getFieldValue({
          fields: unitPlanDetails?.unitPlanFields,
          uid: "unitType",
        }),
        curriculumType,
        resolvedSubjectNodes,
      }),
      "body",
      {}
    );

    const entityType = getEntityTypeForAI({
      curriculumType,
      assessmentType,
      taskTypeGroup,
      groupType,
      entitySubType,
    });

    const _unitPlanDetails = getUnitPlanDetails({
      unitPlanDetails,
      assessmentTemplate,
    });

    const aiAnalysisCriteria = getAIAnalysisItem(assessmentDetails);

    return {
      assessmentDetails,
      aiConversationId,
      assessmentType,
      taskTypeGroup,
      gradeId,
      course,
      groupType,
      entityType,
      templateId,
      unitPlanDetails: ownProps.unitPlanDetails || _unitPlanDetails,
      unitPlanTemplate: ownProps.unitPlanTemplate || unitPlanTemplate,
      assessmentTemplate,
      refetchAssessments: getAssessment.refetch,
      aiAnalysisCriteria,
      isData: !_.isEmpty(assessmentDetails) && isData,
      isLoading:
        _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          _.get(getAssessment, "networkStatus")
        ) || isLoading,
    };
  },
});

const getPlannerElementSetsOfCurriculumVariables = ({
  curriculumId,
  curriculumNodeInterfaceType,
  plannerElementTypes,
  filters,
}) => {
  const convertedFilters = toPlannerElementNodeFilters(filters);
  return {
    id: curriculumId,
    type: curriculumNodeInterfaceType,
    plannerElementNodeSetFilters: { types: plannerElementTypes },
    ...(!_.isEmpty(convertedFilters) && { filters: convertedFilters }),
  };
};

export const getPlannerElementSetsOfCurriculumEnhancer = graphql(
  getPlannerElementSetsOfCurriculumQuery,
  {
    name: "getPlannerElementSetsOfCurriculum",
    skip: ({
      isStepWithPlannerElement,
      isDPLE,
      curriculumType,
      plannerElementTypes,
      skipQuery,
      isStandardsV2Enabled = false,
    }) =>
      (!isStepWithPlannerElement && !isDPLE) ||
      _.isEqual(curriculumType, CURRICULUM_TYPE_PYP) ||
      _.isEmpty(plannerElementTypes) ||
      _.includes(plannerElementTypes, PLANNER_ELEMENT_NESTED_CHECKLIST) ||
      !!skipQuery ||
      isStandardsV2Enabled,
    options: ({
      curriculumId,
      plannerElementTypes,
      curriculumNodeInterfaceType,
      filters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPlannerElementSetsOfCurriculumVariables({
        curriculumId,
        curriculumNodeInterfaceType,
        plannerElementTypes,
        filters,
      }),
    }),
    props({
      getPlannerElementSetsOfCurriculum,
      ownProps: { isData, isLoading },
    }) {
      const curriculumData = _.get(
        getPlannerElementSetsOfCurriculum,
        "node",
        {}
      );
      return {
        plannerElements: normalizePlannerElementNodeSets(
          _.get(curriculumData, "plannerElementNodeSets", {})
        ),
        isData: !_.isEmpty(curriculumData) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getPlannerElementSetsOfCurriculum?.networkStatus
          ) || isLoading,
      };
    },
  }
);

export const getMYPObjectivesAndAchievementLevelsEnhancer = graphql(
  getPlannerElementSetsOfCurriculumQuery,
  {
    name: "getMYPObjectivesAndAchievementLevels",
    skip: ({ curriculumType }) =>
      _.isEqual(curriculumType, CURRICULUM_TYPE_PYP),
    options: ({ curriculumId, elementTypes, curriculumNodeInterfaceType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: curriculumId,
        type: curriculumNodeInterfaceType,
        plannerElementNodeSetFilters: { types: elementTypes },
      },
    }),
    props({
      getMYPObjectivesAndAchievementLevels,
      ownProps: { isData, isLoading, options },
    }) {
      const curriculumData = _.get(
        getMYPObjectivesAndAchievementLevels,
        "node",
        {}
      );

      const plannerElements = normalizePlannerElementNodeSets(
        _.get(curriculumData, "plannerElementNodeSets", {})
      );

      const achievementLevels = _.get(
        _.find(plannerElements, { type: ELEMENT_TYPE_MYP_ACHIEVEMENT_LEVEL }),
        "nodes",
        []
      );

      const { objectiveField } = options;

      const { resolvedValue: { nodes = [] } = {} } = objectiveField || {};

      const objectives = getObjectivesFromNodes({
        nodes,
      });

      return {
        objectives,
        achievementLevels,
        objectiveField,
        isData: !_.isEmpty(curriculumData) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getMYPObjectivesAndAchievementLevels?.networkStatus
          ) || isLoading,
      };
    },
  }
);

const getPypPlannerElementNodesOfCurriculumVariables = ({
  curriculumId,
  plannerElementTypes,
  grades,
  isPypPlannerTheme,
  academicYearId,
  filters,
}) => {
  const convertedFilters = toPlannerElementNodeFilters(filters);
  return {
    id: curriculumId,
    type: NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
    plannerElementNodeSetFilters: { types: plannerElementTypes },
    plannerElementATLFilters: {
      ...convertedFilters,
      ...(grades ? { grades } : {}),
    },
    plannerElementNonAtlFilters: {
      ...convertedFilters,
      ...(isPypPlannerTheme ? { academicYear: academicYearId } : {}),
    },
  };
};

export const getPYPplannerElementNodeOfCurriculumEnhancer = graphql(
  getPypPlannerElementNodesOfCurriculumQuery,
  {
    name: "getPypPlannerElementNodes",
    alias: "getPypPlannerElementNodes",
    skip: ({ curriculumType, plannerElementTypes, skipQuery }) =>
      !_.isEqual(curriculumType, CURRICULUM_TYPE_PYP) ||
      _.isEmpty(plannerElementTypes) ||
      _.includes(plannerElementTypes, ELEMENT_TYPE_PYP_LEARNING_STANDARD) ||
      _.includes(plannerElementTypes, PLANNER_ELEMENT_NESTED_CHECKLIST) ||
      !!skipQuery,
    options: ({
      plannerElementTypes,
      curriculumId,
      grades,
      isPypPlannerTheme,
      academicYearId,
      filters,
    }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: getPypPlannerElementNodesOfCurriculumVariables({
        curriculumId,
        plannerElementTypes,
        grades,
        isPypPlannerTheme,
        academicYearId,
        filters,
      }),
    }),
    props: ({
      getPypPlannerElementNodes: { networkStatus, node },
      ownProps: { isData, isLoading },
    }) => {
      const plannerElementNodeSets = _.get(node, "plannerElementNodeSets", {});

      return {
        isData: !_.isEmpty(node) && isData,
        isLoading:
          _.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus) ||
          isLoading,
        plannerElements: normalizePlannerElementNodeSets(
          plannerElementNodeSets
        ),
      };
    },
  }
);

const getBenchmarkSetOfCurriculumVariables = ({
  curriculumId,
  subjects,
  grades,
  unitPlanIds,
  genericTagIds,
}) => ({
  curriculumId,
  type: NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
  plannerElementNodeSetFilters: { types: [ELEMENT_TYPE_PYP_LEARNING_STANDARD] },
  benchmarkNodeFilters: {
    subjects,
    grades,
    unitPlanIds,
    tags: genericTagIds,
  },
});

export const getBenchmarkSetOfCurriculumEnhancer = graphql(
  getBenchmarkSetOfCurriculumQuery,
  {
    name: "getBenchmarkSetOfCurriculum",
    skip: ({
      curriculumType,
      plannerElementType,
      skipQuery,
      isStandardsV2Enabled,
    }) =>
      !_.isEqual(curriculumType, CURRICULUM_TYPE_PYP) ||
      !_.isEqual(plannerElementType, ELEMENT_TYPE_PYP_LEARNING_STANDARD) ||
      !!skipQuery ||
      isStandardsV2Enabled,
    options: ({
      curriculumId,
      subjects,
      grades,
      unitPlanIds,
      genericTagIds,
    }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: getBenchmarkSetOfCurriculumVariables({
        curriculumId,
        subjects,
        grades,
        unitPlanIds,
        genericTagIds,
      }),
    }),
    props({ getBenchmarkSetOfCurriculum, ownProps: { isData, isLoading } }) {
      const curriculumData = _.get(getBenchmarkSetOfCurriculum, "node", {});
      const plannerElements = normalizePlannerElementNodeSets(
        _.get(curriculumData, "plannerElementNodeSets", {})
      );
      const nodes = _.get(_.head(plannerElements), "nodes", []);

      return {
        nodes,
        isData: !_.isEmpty(plannerElements) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getBenchmarkSetOfCurriculum?.networkStatus
          ) || isLoading,
      };
    },
  }
);

export const getCurriculumStandardsQueryEnhancer = graphql(
  getCurriculumStandardsQuery,
  {
    name: "getCurriculumStandards",
    skip: ({ plannerElementType, skipQuery, isStandardsV2Enabled }) =>
      !_.includes(
        [
          ELEMENT_TYPE_PYP_LEARNING_STANDARD,
          ELEMENT_TYPE_MYP_LEARNING_STANDARD,
          ELEMENT_TYPE_DP_LEARNING_STANDARD,
          ELEMENT_TYPE_UBD_LEARNING_STANDARD,
        ],
        plannerElementType
      ) ||
      !!skipQuery ||
      !isStandardsV2Enabled,
    options: ({
      curriculumId,
      curriculumType,
      plannerElementNodeFilters,
      plannerElementNodeSetFilters,
      orderBy,
      orderByDirection,
    }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        id: curriculumId,
        plannerElementNodeFilters,
        plannerElementNodeSetFilters,
        isPYPCurriculum: curriculumType === CURRICULUM_TYPE_PYP,
        portalType: "PLANNER",
        orderBy,
        orderByDirection,
      },
    }),
    props({ getCurriculumStandards, ownProps: { isData, isLoading } }) {
      const data = _.get(getCurriculumStandards, "node", {});
      const plannerElementNodeSets = _.map(
        _.get(data, "plannerElementNodeSets.edges", []),
        "node"
      );
      const nodes = withComputedIsLeafForNodes(
        _.flatMap(plannerElementNodeSets, nodeSet => {
          const wrapper = _.get(nodeSet, "plannerElementNodeSetWrapperV2", {});
          const standardsEdges =
            _.get(wrapper, "publishedData.edges") ||
            _.get(wrapper, "publishedBenchmarks.edges") ||
            [];
          return _.map(standardsEdges, "node");
        })
      );

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getCurriculumStandards?.networkStatus
          ) || isLoading,
        nodes,
      };
    },
  }
);
